import { useEffect, useRef, useState } from "react";
import { LogOut, Maximize, Minimize, MonitorPlay } from "lucide-react";
import { useTheatre } from "../site/SiteLayout";

const BTN = "rounded border px-3 py-1.5 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold";
const on = "border-gold bg-gold text-navy font-semibold";
const off = "border-white/40 hover:bg-white/10";

// Fullscreen targets the whole document (with theatre layout) rather than the stage element: Zoom component view renders
// some of its panels (chat, settings, menus) outside the root, and those would be hidden outside a fullscreen stage.
const LABEL = { joined: "Live session", reconnecting: "Reconnecting…", waiting: "Waiting for the host to admit you…", connecting: "Connecting to Zoom…", loading: "Preparing the meeting…" };

export function StageControls({ phase, onLeave }) {
  const joined = phase === "joined";
  const { theatre, setTheatre } = useTheatre();
  const [full, setFull] = useState(!!document.fullscreenElement);
  const before = useRef(false);
  useEffect(() => {
    const change = () => {
      const isFull = !!document.fullscreenElement;
      setFull(isFull);
      if (!isFull) setTheatre(before.current); // Esc or button: restore the layout used before fullscreen
    };
    document.addEventListener("fullscreenchange", change);
    return () => document.removeEventListener("fullscreenchange", change);
  }, [setTheatre]);
  const toggleFull = async () => {
    if (document.fullscreenElement) return document.exitFullscreen();
    before.current = theatre;
    setTheatre(true);
    try { await document.documentElement.requestFullscreen(); } catch { setTheatre(before.current); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 bg-navy px-3 py-2 text-white" data-testid="meeting-controls">
      <span className="mr-auto flex items-center gap-2 text-sm font-semibold" role="status" aria-live="polite" data-testid="meeting-live-status">
        <span className={`h-2 w-2 rounded-full ${joined ? "bg-red-500" : "animate-pulse bg-gold"}`} aria-hidden="true" /> {LABEL[phase] || "Connecting…"}</span>
      <button type="button" className={`${BTN} ${theatre && !full ? on : off}`} onClick={() => setTheatre(!theatre)} disabled={full} aria-pressed={theatre} data-testid="theatre-toggle-btn">
        <MonitorPlay size={16} className="mr-1 inline" aria-hidden="true" />{theatre ? "Exit theatre" : "Theatre mode"}</button>
      {document.fullscreenEnabled && <button type="button" className={`${BTN} ${full ? on : off}`} onClick={toggleFull} aria-pressed={full} data-testid="fullscreen-toggle-btn">
        {full ? <Minimize size={16} className="mr-1 inline" aria-hidden="true" /> : <Maximize size={16} className="mr-1 inline" aria-hidden="true" />}{full ? "Exit fullscreen" : "Fullscreen"}</button>}
      <button type="button" className="rounded bg-burgundy px-3 py-1.5 text-sm font-semibold hover:opacity-90" onClick={() => onLeave()} data-testid="meeting-leave-btn">
        <LogOut size={16} className="mr-1 inline" aria-hidden="true" />Leave</button>
    </div>
  );
}
